import { useState } from "react";
import {
  Button,
  Drawer,
  DrawerSection,
  FailedCallout,
  ReasonField,
  TextArea,
  cx,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import { IMPORT_FLAG_LABELS, type ImportPreview, type ImportRow } from "../../contracts/index";
import { useProfileInvalidation } from "../invalidation";
import { profileCommands } from "../queries";

const ACCOUNT_STATE_LABELS: Record<ImportRow["accounts"][number]["state"], string> = {
  collected: "suggested source",
  not_collected: "known account",
  unsupported: "recorded only",
  invalid: "invalid",
};

function createBlocked(preview: ImportPreview | null, lines: readonly number[]): string | null {
  if (!preview) return "Check the rows first";
  if (lines.length === 0) return "Select at least one row";
  return null;
}

function PreviewSection({
  preview,
  lines,
  setLines,
}: {
  preview: ImportPreview;
  lines: number[];
  setLines: (next: number[]) => void;
}) {
  return (
    <DrawerSection title={`Preview · ${String(preview.selectable)} of ${String(preview.rows.length)} can be created`}>
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {preview.rows.map((r) => (
          <li
            key={r.line}
            className={cx("rounded-lg border border-divider px-2.5 py-2", !r.selectable && "bg-surface-2")}
          >
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                disabled={!r.selectable}
                checked={lines.includes(r.line)}
                onChange={(e) => {
                  setLines(e.target.checked ? [...lines, r.line] : lines.filter((l) => l !== r.line));
                }}
              />
              <span className="min-w-0">
                <b className="font-strong">{r.name || "(no name)"}</b>{" "}
                <span className="text-muted">· row {r.line}</span>
                {r.accounts.length > 0 ? (
                  <span className="block truncate text-md text-muted">
                    {r.accounts.map((a) => `${a.platform ?? "?"}: ${ACCOUNT_STATE_LABELS[a.state]}`).join(" · ")}
                  </span>
                ) : null}
                {r.flags.map((f) => (
                  <span
                    key={`${f.kind}-${f.detail}`}
                    className={cx(
                      "block text-md",
                      f.kind === "uncollected_account" || f.kind === "unsupported_account"
                        ? "text-muted"
                        : "text-waiting",
                    )}
                  >
                    {IMPORT_FLAG_LABELS[f.kind]}: {f.detail}
                  </span>
                ))}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <p className="m-0 mt-2 text-md text-muted">
        Each row becomes a Draft; accounts on collected platforms become suggested sources, others known accounts.
        Source discovery then drafts a first plan; nothing is monitored until tracking starts.
      </p>
    </DrawerSection>
  );
}

export function ImportPanel({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const invalidation = useProfileInvalidation();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [lines, setLines] = useState<number[]>([]);
  const reason = useReason();
  const check = useCommandMutation({
    run: (csrf) => profileCommands.previewImport({ text }, csrf),
    ...invalidation,
    onSuccess: (p) => {
      setPreview(p);
      setLines(p.rows.filter((r) => r.selectable).map((r) => r.line));
    },
  });
  const create = useCommandMutation({
    run: (csrf, reason: string) => profileCommands.importRoster({ text, lines, languages: ["en"], reason }, csrf),
    ...invalidation,
    onSuccess: (r) => {
      toast.show(`${String(r.created.length)} draft${r.created.length === 1 ? "" : "s"} created`);
      onClose();
    },
  });
  return (
    <Drawer
      title="Import profiles"
      onClose={onClose}
      footer={
        <span className="flex items-center justify-end gap-2">
          <span className="mr-auto text-md text-muted">Nothing changes until you confirm.</span>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            type="submit"
            form="import-form"
            disabledReason={createBlocked(preview, lines)}
            aria-busy={create.isPending || undefined}
          >
            Create drafts
          </Button>
        </span>
      }
    >
      <form
        id="import-form"
        noValidate
        className="flex flex-col gap-4 text-ui"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.submit() && lines.length > 0) create.mutate(reason.trimmed);
        }}
      >
        <DrawerSection title="Rows">
          <TextArea
            aria-label="Rows to import"
            className="min-h-[110px] font-mono text-md"
            placeholder={
              "name,wikipedia,accounts\nKeir Starmer,https://en.wikipedia.org/wiki/Keir_Starmer,https://x.com/Keir_Starmer"
            }
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setPreview(null);
            }}
          />
          <label className="mt-1.5 block text-md text-muted">
            Or upload a CSV:{" "}
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file)
                  void file.text().then((t) => {
                    setText(t);
                    setPreview(null);
                  });
              }}
            />
          </label>
          <Button
            size="sm"
            className="mt-2"
            disabledReason={text.trim() ? null : "Paste rows or choose a file"}
            onClick={() => {
              check.mutate();
            }}
            aria-busy={check.isPending || undefined}
          >
            Check rows
          </Button>
        </DrawerSection>
        {preview ? <PreviewSection preview={preview} lines={lines} setLines={setLines} /> : null}
        <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
        <FailedCallout error={check.error ?? create.error} className="" />
      </form>
    </Drawer>
  );
}
